import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Dropdown } from "primereact/dropdown";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import * as estimationService from "../../services/estimationService";
import StagedPartsSection from "../Vendor_bookings/StagedPartsSection";
import { PillButton, TintButton } from "./EstimationButtons";
import { google_places_key } from "../../constants/Constants";
import {
  countryMatchProfileTest,
  validMobileFormatByOwnPrefixTest
} from "../../utils/phoneValidation";

// Defensive against a stale/corrupted localStorage value — e.g. some other
// flow calling setItem with an actual `undefined` (coerced to the literal
// string "undefined", not real undefined). JSON.parse throws on that, and
// since this runs at module load (not inside a component), an uncaught
// throw here previously crashed the whole bundle instead of just this page.
const safeParseLocalStorage = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (raw == null || raw === "undefined") return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const vendor_id = safeParseLocalStorage("userId", null);
const vendorProfile = safeParseLocalStorage("vendorProfile", {});

// Backend sometimes stores missing values as the literal string "null" -
// same helper Bookings.jsx uses, so autofilled values from get_users_details
// never render/validate as the word "null".
const cleanBackendValue = (v) =>
  v === null || v === undefined || String(v).trim().toLowerCase() === "null"
    ? ""
    : v;

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Local (not UTC) date parts, so "today"/"tomorrow" match the vendor's own
// calendar - valid_until is a bare DATE with no time/tz component, and
// comparing via UTC getters here would shift the day in timezones ahead of
// UTC (e.g. UAE, UTC+4).
const toLocalDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const emptyValues = {
  customer_id: null,
  customer_name: "",
  phone_number: "",
  email: "",
  house_no: "",
  area: "",
  location: "",
  location_id: "",
  state: "",
  landmark: "",
  country: vendorProfile?.currency === "INR" ? 2 : 1,
  lat: "",
  lng: "",
  discount_type: "FLAT",
  discount_value: 0,
  tax_pct: parseFloat(vendorProfile?.tax || 0),
  valid_until: "",
  notes: ""
};

// Rebuilds "New Booking"'s real Service Category -> Sub Category -> Offering
// picker (Bookings.jsx ~3555-3697) and its real Customer Information /
// Full Address section (phone lookup via get_users_details, Google Places
// autocomplete) - an estimate is drafted in the same fast, low-friction
// context as a booking, so this deliberately reuses the same mechanics
// rather than a simplified stand-in.
function EstimationForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState("DRAFT");
  const [displayId, setDisplayId] = useState("");
  const [version, setVersion] = useState(1);

  const [currency, setCurrency] = useState(vendorProfile?.currency || "AED");
  const [industry, setIndustry] = useState([]);
  const isSportsService = industry?.industry === 7;
  const isCarService = industry?.industry === 8;
  const isSalonService = industry?.industry === 9;
  const addressRequired = !(isSportsService || isCarService || isSalonService);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [locationError, setLocationError] = useState("");
  const [recentBookings, setRecentBookings] = useState([]);

  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [groupedData, setGroupedData] = useState({});
  const [activeType, setActiveType] = useState("null");

  const [parts, setParts] = useState([]);

  const latestUserLookupRef = useRef(0);
  const autocompleteRef = useRef(null);

  const minValidUntil = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toLocalDateString(d);
  }, []);

  /* ---------------------------------------------------------------- *
   *  Formik - mirrors Bookings.jsx's Customer Information / Full
   *  Address validation rules, minus schedule/crew/partner concepts
   *  that don't apply to an estimate.
   * ---------------------------------------------------------------- */
  const validationSchema = Yup.object().shape({
    phone_number: Yup.string()
      .required("Mobile Number is required")
      .test("country-match-profile", countryMatchProfileTest(currency))
      .test("valid-mobile-format", validMobileFormatByOwnPrefixTest()),
    customer_name: Yup.string()
      .required("Customer name is required")
      .min(2, "Customer name must be at least 2 characters")
      .max(20, "Customer name must not exceed 20 characters"),
    house_no: addressRequired
      ? Yup.string().required("Street / House no. is required").max(100)
      : Yup.string().max(100),
    area: addressRequired ? Yup.string().required("Area is required") : Yup.string(),
    location: addressRequired ? Yup.string().required("Address is required") : Yup.string(),
    state: addressRequired
      ? Yup.string().required(currency === "INR" ? "State is required" : "Emirate is required")
      : Yup.string(),
    country: addressRequired ? Yup.string().required("Country is required") : Yup.string(),
    lat: Yup.string()
      .nullable()
      .test("is-valid-latitude", "Invalid latitude value", (v) => {
        if (!v) return true;
        const n = parseFloat(v);
        return !isNaN(n) && n >= -90 && n <= 90;
      }),
    lng: Yup.string()
      .nullable()
      .test("is-valid-longitude", "Invalid longitude value", (v) => {
        if (!v) return true;
        const n = parseFloat(v);
        return !isNaN(n) && n >= -180 && n <= 180;
      }),
    email: Yup.string()
      .nullable()
      .test("valid-email", "Enter valid email (.com, .co.in only)", (v) => {
        if (!v) return true;
        return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|co\.in)$/.test(v);
      }),
    valid_until: Yup.string()
      .required("Validity date is required")
      .test("is-future-date", "Validity date must be a future date", (v) => {
        if (!v) return true;
        return v > toLocalDateString(new Date());
      })
  });

  const formik = useFormik({
    initialValues: emptyValues,
    enableReinitialize: true,
    validationSchema,
    // handleSaveClick below validates and calls doSave() directly rather
    // than going through submitForm() - this onSubmit is unreachable but
    // useFormik requires one.
    onSubmit: () => {}
  });
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    validateForm,
    setTouched
  } = formik;

  /* ---------------------------------------------------------------- *
   *  Vendor profile (currency + industry) - industry must come from a
   *  fresh ezpMyProfile fetch, not localStorage, same as Bookings.jsx.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    apicall
      .get("ezpMyProfile")
      .then((res) => {
        const profile = res?.data?.[0];
        if (profile) {
          setCurrency(profile.currency || "AED");
          setIndustry(profile);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    apicall
      .get("country")
      .then((res) => {
        if (res.success) setCountries(Array.isArray(res.data) ? res.data : res.data?.countries || []);
      })
      .catch(() => setCountries([]));
  }, []);

  const fetchStates = useCallback(() => {
    const countryId = currency === "INR" ? 2 : 1;
    apicall
      .get(`state/${countryId}`)
      .then((res) => setStates(res.success && res.data ? res.data : []))
      .catch(() => setStates([]));
  }, [currency]);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  /* ---------------------------------------------------------------- *
   *  Services / offerings - get_ezp_service?partner_id=X is an INNER JOIN
   *  against ezp_vendor_price.partner, so partner_id must be a REAL
   *  partner id for this vendor, never a hardcoded guess (that was the
   *  root cause of the picker showing nothing). Mirrors Bookings.jsx's
   *  fetchPartners(): GET partners, then use the first partner's real id.
   * ---------------------------------------------------------------- */
  const [partnerId, setPartnerId] = useState(null);

  useEffect(() => {
    apicall
      .get("partners")
      .then((res) => {
        const firstId = res?.data?.[0]?.id;
        if (firstId) setPartnerId(firstId);
        else toast.error("No partner profile found for this vendor - cannot load services.");
      })
      .catch(() => toast.error("Failed to load partner profile"));
  }, []);

  useEffect(() => {
    if (!partnerId) return;
    apicall
      .get(`get_ezp_service?partner_id=${partnerId}`)
      .then((res) => {
        if (res.success && res.data) setServices(res.data);
        else setServices([]);
      })
      .catch(() => setServices([]));
  }, [partnerId]);

  async function loadOfferings(serviceId, addedOfferingRows = []) {
    if (!partnerId) return [];
    try {
      const response = await apicall.get(
        `offeringFromService?service_id=${serviceId}&&is_partner=true&&partner_id=${partnerId}`
      );
      const rows = (response?.data || []).filter((r) => Number(r.is_active) === 1);
      const addedIds = addedOfferingRows.map((o) => o.service_offering_id);
      const withState = rows.map((row) => {
        const match = addedOfferingRows.find((o) => o.service_offering_id === row.service_offering_id);
        return {
          ...row,
          added: addedIds.includes(row.service_offering_id),
          quantity: match ? Number(match.quantity) : 1,
          unit_price: match ? Number(match.unit_price) : Number(row.price) || 0
        };
      });
      // Group by service_type_name - mirrors Bookings.jsx's arrangeData()
      const grouped = withState.reduce((acc, item) => {
        const key = item.service_type_name;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});
      setGroupedData(grouped);
      const firstAdded = withState.find((o) => o.added);
      setActiveType(firstAdded ? firstAdded.service_type_name : Object.keys(grouped)[0] || "null");
    } catch {
      setGroupedData({});
    }
  }

  async function handleSelectService(service) {
    setSelectedServiceId(service.service_id);
    await loadOfferings(service.service_id, []);
  }

  function toggleOffering(offeringId) {
    setGroupedData((prev) => {
      const copy = {};
      for (const key of Object.keys(prev)) {
        copy[key] = prev[key].map((o) =>
          o.service_offering_id === offeringId ? { ...o, added: !o.added } : o
        );
      }
      return copy;
    });
  }

  function updateOfferingField(offeringId, field, value) {
    setGroupedData((prev) => {
      const copy = {};
      for (const key of Object.keys(prev)) {
        copy[key] = prev[key].map((o) => (o.service_offering_id === offeringId ? { ...o, [field]: value } : o));
      }
      return copy;
    });
  }

  const allOfferings = Object.values(groupedData).flat();
  const addedOfferings = allOfferings.filter((o) => o.added);

  /* ---------------------------------------------------------------- *
   *  Customer phone lookup - mirrors Bookings.jsx's get_user_details /
   *  debouncedCheckUserExists exactly (NOT a "click a recent booking to
   *  autofill" dropdown - that doesn't exist in New Booking either).
   * ---------------------------------------------------------------- */
  const getUserDetails = async (phone) => {
    const requestId = ++latestUserLookupRef.current;
    try {
      const response = await apicall.get(`get_users_details/${phone}`);
      if (requestId !== latestUserLookupRef.current) return;
      if (response.status === 200 && response.data?.length > 0) {
        const user = response.data[0];
        const fullName = `${user.first_name || ""} ${user.middle_name || ""} ${user.last_name || ""}`
          .replace(/\s+/g, " ")
          .trim();
        setValues((prev) => ({
          ...prev,
          customer_id: user?.id || null,
          customer_name: fullName,
          email: cleanBackendValue(user?.email),
          house_no: cleanBackendValue(user?.house_no),
          area: cleanBackendValue(user?.area),
          landmark: cleanBackendValue(user?.landmark),
          state: cleanBackendValue(user?.state),
          location: cleanBackendValue(user?.location),
          lat: user?.lat || "",
          lng: user?.lng || "",
          location_id: user?.location_id || ""
        }));
      } else {
        setFieldValue("customer_id", null);
      }
    } catch {
      // Lookup failing is not fatal - the vendor can still fill the form manually.
    }
  };

  const getRecentBookingsInfo = async (phone) => {
    try {
      const res = await apicall.get(`getRecentBookings/${phone}/${vendor_id}`);
      setRecentBookings(res.success && res.data ? res.data : []);
    } catch {
      setRecentBookings([]);
    }
  };

  const debouncedCheckUserExists = useCallback(
    debounce((phone) => {
      getUserDetails(phone);
      getRecentBookingsInfo(phone);
    }, 500),
    []
  );

  /* ---------------------------------------------------------------- *
   *  Google Places autocomplete - mirrors Bookings.jsx's onLoad/
   *  onPlaceChanged exactly, including the country-mismatch block.
   * ---------------------------------------------------------------- */
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: google_places_key, libraries: ["places"] });
  const onAutocompleteLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };
  const onPlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.address_components) return;

    let street = place.formatted_address || "";
    let area = "";
    let state = "";
    let country = "";
    let lat = place.geometry?.location?.lat();
    let lng = place.geometry?.location?.lng();

    place.address_components.forEach((comp) => {
      const types = comp.types;
      if (types.includes("sublocality") || types.includes("locality")) area = comp.long_name;
      if (types.includes("administrative_area_level_1")) state = comp.long_name;
      if (types.includes("country")) country = comp.short_name?.toLowerCase();
    });

    const expectedCountry = currency === "INR" ? "in" : "ae";
    if (!country || country !== expectedCountry) {
      setLocationError(
        currency === "INR" ? "Please select a location within India" : "Please select a location within UAE"
      );
      setFieldValue("location", "");
      setFieldValue("area", "");
      setFieldValue("state", "");
      setFieldValue("lat", "");
      setFieldValue("lng", "");
      return;
    }
    setLocationError("");
    setFieldValue("location", street);
    setFieldValue("area", area);
    setFieldValue("state", state);
    setFieldValue("country", country);
    setFieldValue("lat", lat);
    setFieldValue("lng", lng);
  };

  /* ---------------------------------------------------------------- *
   *  Edit mode: load existing estimation
   * ---------------------------------------------------------------- */
  useEffect(() => {
    // Waits for partnerId too - loadOfferings() below needs a real partner
    // id (see the Services/offerings section above) or it silently no-ops.
    if (!isEdit || !partnerId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await estimationService.getEstimation(id);
        if (!res.success || !res.data) {
          toast.error(res.message || "Quotation not found");
          navigate("/estimations");
          return;
        }
        const e = res.data;
        setValues({
          customer_id: e.customer_id,
          customer_name: e.customer_name || "",
          phone_number: e.phone_number || "",
          email: e.email || "",
          house_no: e.house_no || "",
          area: e.area || "",
          location: e.location || "",
          location_id: "",
          state: e.state || "",
          landmark: e.landmark || "",
          country: e.country || (currency === "INR" ? 2 : 1),
          lat: e.lat || "",
          lng: e.lng || "",
          discount_type: e.discount_type || "FLAT",
          discount_value: Number(e.discount_value) || 0,
          tax_pct: Number(e.tax_pct) || 0,
          valid_until: e.valid_until ? String(e.valid_until).split("T")[0] : "",
          notes: e.notes || ""
        });
        setStatus(e.status);
        setDisplayId(e.display_estimation_id);
        setVersion(e.version);
        setSelectedServiceId(e.service_id || "");
        setParts(
          (e.items || []).map((i) => ({
            id: i.id,
            item_id: i.item_id,
            name: i.item_name,
            unit_of_measure: i.unit_of_measure,
            quantity: Number(i.quantity),
            unit_price: Number(i.unit_price),
            amount: Number(i.quantity) * Number(i.unit_price)
          }))
        );
        if (e.service_id) await loadOfferings(e.service_id, e.offerings || []);
      } catch (err) {
        toast.error("Failed to load quotation");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, partnerId]);

  /* ---------------------------------------------------------------- *
   *  Pricing preview - mirrors estimation.service.js's recomputeTotals.
   * ---------------------------------------------------------------- */
  const offeringsSubtotal = addedOfferings.reduce((s, o) => s + Number(o.quantity) * Number(o.unit_price), 0);
  const partsSubtotal = parts.reduce((s, p) => s + Number(p.amount || 0), 0);
  const subtotal = offeringsSubtotal + partsSubtotal;
  const discountAmount =
    values.discount_type === "PERCENT" ? (subtotal * Number(values.discount_value || 0)) / 100 : Number(values.discount_value || 0);
  const taxable = Math.max(subtotal - discountAmount, 0);
  const taxAmount = (taxable * Number(values.tax_pct || 0)) / 100;
  const grandTotal = taxable + taxAmount;

  function handlePartAdd(item, quantity, unitPrice) {
    setParts((prev) => [
      ...prev,
      {
        id: `tmp_${Date.now()}`,
        item_id: item.id,
        name: item.name,
        unit_of_measure: item.unit_of_measure,
        quantity,
        unit_price: unitPrice,
        amount: quantity * unitPrice
      }
    ]);
  }

  function handlePartRemove(rowId) {
    setParts((prev) => prev.filter((p) => p.id !== rowId));
  }

  // Line items (offerings + parts) are always fully re-synced against
  // whatever is currently on the estimation, rather than diffed - a quote
  // has only a handful of lines, so "clear then re-add the current local
  // set" is simpler than per-row dirty tracking and always correct.
  async function syncLines(estimationId) {
    const fresh = await estimationService.getEstimation(estimationId);
    const existingOfferings = fresh?.data?.offerings || [];
    const existingItems = fresh?.data?.items || [];

    await Promise.all(existingOfferings.map((o) => estimationService.removeOffering(estimationId, o.id)));
    await Promise.all(existingItems.map((i) => estimationService.deleteItem(estimationId, i.id)));

    for (const o of addedOfferings) {
      await estimationService.addOffering(estimationId, {
        service_id: selectedServiceId,
        service_type_id: o.service_type_id,
        service_offering_id: o.service_offering_id,
        quantity: o.quantity,
        unit_price: o.unit_price
      });
    }
    for (const p of parts) {
      await estimationService.addItem(estimationId, {
        item_id: p.item_id,
        quantity: p.quantity,
        unit_price: p.unit_price
      });
    }
  }

  async function doSave(formValues, thenSend) {
    setSaving(true);
    try {
      const payload = { ...formValues, service_id: selectedServiceId || null, currency };
      let workingId = id;
      if (!isEdit) {
        const createRes = await estimationService.createEstimation(payload);
        if (!createRes.success) throw new Error(createRes.message || "Failed to create quotation");
        workingId = createRes.data.id;
      } else {
        const updateRes = await estimationService.updateEstimation(id, payload);
        if (!updateRes.success) throw new Error(updateRes.message || "Failed to update quotation");
        workingId = updateRes.data.id;
      }

      await syncLines(workingId);

      if (thenSend) {
        const sendRes = await estimationService.sendEstimation(workingId);
        if (!sendRes.success) throw new Error(sendRes.message || "Failed to send quotation");
      }

      toast.success(thenSend ? "Quotation sent" : "Quotation saved");
      navigate(`/estimations/${workingId}`);
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  }

  // Calls doSave directly once validated, instead of relying on Formik's
  // submitForm()/onSubmit (which silently no-ops on validation failure with
  // no feedback at all if a field's error has no visible Feedback element
  // wired up) - surfacing the actual blocking error is what makes "the
  // button does nothing" debuggable instead of a dead end.
  async function handleSaveClick(thenSend) {
    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      setTouched(
        Object.keys(values).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
        false
      );
      toast.error(Object.values(formErrors)[0]);
      return;
    }
    if (!selectedServiceId) {
      toast.error("Please select a service");
      return;
    }
    // Sub Category is optional for some services - not every service has
    // one, so it must never block Save. Service Type (addedOfferings) below
    // already guarantees at least one selection was made either way.
    if (addedOfferings.length === 0) {
      toast.error("Please select a service type");
      return;
    }
    await doSave(values, thenSend);
  }

  const isLocked = ["ACCEPTED", "DECLINED", "CONVERTED"].includes(status);
  const stateLabel = currency === "INR" ? "State" : "Emirate";

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          {isEdit ? `Edit Quotation ${displayId}${version > 1 ? ` (v${version})` : ""}` : "New Quotation"}
        </h4>
      </div>

      {isLocked && (
        <div className="alert alert-warning">
          This quotation is {status.toLowerCase()} and can no longer be edited.
        </div>
      )}

      <Row className="g-3">
      <Col xs={12} lg={8}>
      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-3">Customer Information</h6>
          <Row className="g-3 mb-3">
            <Col xs={12} md={4}>
              <Form.Label>
                Mobile Number <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <PhoneInput
                country={currency === "INR" ? "in" : "ae"}
                onlyCountries={["ae", "in"]}
                value={values.phone_number}
                disabled={isLocked}
                onChange={(value) => {
                  const cleaned = value.replace(/\D/g, "");
                  setFieldValue("phone_number", cleaned);
                  if (cleaned.length >= 12) debouncedCheckUserExists(cleaned);
                }}
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  backgroundColor: "white",
                  borderColor: touched.phone_number && errors.phone_number ? "#dc3545" : "#E9ECEF"
                }}
                inputProps={{ name: "phone_number" }}
              />
              {touched.phone_number && errors.phone_number && (
                <div className="invalid-feedback d-block">{errors.phone_number}</div>
              )}
            </Col>
            <Col xs={12} md={4}>
              <Form.Label>
                Customer Name <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Form.Control
                name="customer_name"
                value={values.customer_name}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked}
                isInvalid={touched.customer_name && !!errors.customer_name}
              />
              <Form.Control.Feedback type="invalid">{errors.customer_name}</Form.Control.Feedback>
            </Col>
            <Col xs={12} md={4}>
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked}
                isInvalid={touched.email && !!errors.email}
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Col>
          </Row>

          <h6 className="section-heading mb-2">Full Address</h6>
          <Row className="g-3">
            <Col xs={12} md={4}>
              <Form.Label>
                Address {addressRequired && <span style={{ color: "red" }}>*</span>}
              </Form.Label>
              <div className="d-flex align-items-center gap-2">
                <div style={{ flex: 1 }}>
                  {isLoaded ? (
                    <Autocomplete
                      onLoad={onAutocompleteLoad}
                      onPlaceChanged={onPlaceChanged}
                      options={{ componentRestrictions: { country: currency === "INR" ? "in" : "ae" } }}
                    >
                      <Form.Control
                        type="text"
                        name="location"
                        placeholder="Search address"
                        value={values.location}
                        onChange={handleChange}
                        disabled={isLocked}
                        isInvalid={touched.location && !!errors.location}
                      />
                    </Autocomplete>
                  ) : (
                    // Falls back to a plain input if the Google Maps script
                    // hasn't loaded (slow network, blocked script, bad key) -
                    // otherwise this field would be entirely invisible while
                    // still being required, permanently blocking submission
                    // with no way for the vendor to fill it in.
                    <Form.Control
                      type="text"
                      name="location"
                      placeholder="Enter address"
                      value={values.location}
                      onChange={handleChange}
                      disabled={isLocked}
                      isInvalid={touched.location && !!errors.location}
                    />
                  )}
                </div>
                {values.location && !isLocked && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => {
                      setFieldValue("location", "");
                      setFieldValue("area", "");
                      setFieldValue("state", "");
                      setFieldValue("lat", "");
                      setFieldValue("lng", "");
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              {locationError && <div className="invalid-feedback d-block">{locationError}</div>}
              {touched.location && errors.location && (
                <div className="invalid-feedback d-block">{errors.location}</div>
              )}
            </Col>
            <Col xs={12} md={4}>
              <Form.Label>
                House / Flat / Building No {addressRequired && <span style={{ color: "red" }}>*</span>}
              </Form.Label>
              <Form.Control
                name="house_no"
                value={values.house_no}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked}
                isInvalid={touched.house_no && !!errors.house_no}
              />
              <Form.Control.Feedback type="invalid">{errors.house_no}</Form.Control.Feedback>
            </Col>
            <Col xs={12} md={4}>
              <Form.Label>
                Area {addressRequired && <span style={{ color: "red" }}>*</span>}
              </Form.Label>
              <Form.Control
                name="area"
                value={values.area}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked}
                isInvalid={touched.area && !!errors.area}
              />
              <Form.Control.Feedback type="invalid">{errors.area}</Form.Control.Feedback>
            </Col>
            <Col xs={12} md={4}>
              <Form.Label>
                {stateLabel} {addressRequired && <span style={{ color: "red" }}>*</span>}
              </Form.Label>
              <Dropdown
                name="state"
                value={values.state}
                options={states?.map((s) => ({ label: s.name, value: s.name }))}
                placeholder={`Select ${stateLabel}`}
                disabled={isLocked}
                onChange={(e) => setFieldValue("state", e.value)}
                className={`w-100 ${touched.state && errors.state ? "is-invalid" : ""}`}
                style={{ height: "40px" }}
              />
              {touched.state && errors.state && <div className="invalid-feedback d-block">{errors.state}</div>}
            </Col>
            <Col xs={12} md={4}>
              <Form.Label>Landmark</Form.Label>
              <Form.Control name="landmark" value={values.landmark} onChange={handleChange} disabled={isLocked} />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label>Country {addressRequired && <span style={{ color: "red" }}>*</span>}</Form.Label>
              <Dropdown
                name="country"
                value={currency === "INR" ? 2 : 1}
                options={countries?.map((c) => ({ label: c.name, value: Number(c.country_id) }))}
                placeholder="Select Country"
                disabled={isLocked}
                onChange={(e) => {
                  const expected = currency === "INR" ? 2 : 1;
                  if (Number(e.value) !== expected) {
                    toast.error(
                      currency === "INR"
                        ? "Please select a country related to your profile (India)."
                        : "Please select a country related to your profile (UAE)."
                    );
                    return;
                  }
                  setFieldValue("country", e.value);
                }}
                className="w-100"
                style={{ height: "40px" }}
              />
            </Col>
          </Row>

          {recentBookings.length > 0 && (
            <div className="mt-4">
              <h6 className="section-heading mb-2">Recent Bookings</h6>
              <div style={{ overflowX: "auto" }}>
                <Table size="sm">
                  <thead>
                    <tr>
                      <th>Booking Id</th>
                      <th>Service</th>
                      <th>Date</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentBookings.slice(0, 5).map((b, idx) => (
                      <tr key={idx}>
                        <td>{b.display_booking_id}</td>
                        <td>{[b.service_name, b.service_type_name, b.service_offering].filter(Boolean).join(" - ")}</td>
                        <td>{b.service_date ? String(b.service_date).split("T")[0] : ""}</td>
                        <td className="text-end">{Number(b.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-3">
            Service Category <span style={{ color: "red" }}>*</span>
          </h6>
          <div className="service-selection-card mb-4">
            <div className="d-flex gap-3 flex-wrap">
              {services.map((s) => (
                <div
                  key={s.service_id}
                  onClick={() => !isLocked && handleSelectService(s)}
                  className={`service-type-pill ${String(s.service_id) === String(selectedServiceId) ? "active" : ""}`}
                >
                  {s.service_name}
                </div>
              ))}
            </div>

            {Object.keys(groupedData).length > 0 && !Object.keys(groupedData).includes("null") && (
              <div className="mt-4">
                <h6 className="section-title">Sub Category</h6>
                <div className="d-flex gap-3 flex-wrap">
                  {Object.keys(groupedData).map((key) => {
                    const hasAdded = groupedData[key]?.some((d) => d?.added);
                    return (
                      <div
                        key={key}
                        onClick={() => setActiveType(key)}
                        className={`service-type-pill ${key === activeType || hasAdded ? "active" : ""}`}
                      >
                        {key}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {Object.keys(groupedData).length > 0 && (
              <div className="mt-4">
                <h6 className="section-title">Service Type</h6>
                <div className="row g-3">
                  {(groupedData[activeType] || []).map((o) => (
                    <div className="col-md-3" key={o.service_offering_id}>
                      <div
                        onClick={() => !isLocked && toggleOffering(o.service_offering_id)}
                        className="offering-row-card d-flex justify-content-between align-items-center"
                      >
                        <div className="fw-semibold">{o.service_offering_name}</div>
                        <div className={`btn btn-sm rounded-3 ${o.added ? "btn-danger" : "btn-outline-danger"}`}>
                          {o.added ? "Added" : "Add"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      <StagedPartsSection parts={parts} currency={currency} onAdd={handlePartAdd} onRemove={handlePartRemove} />
      </Col>

      <Col xs={12} lg={4}>
        {/* position:sticky only applies at >=lg (see the scoped <style> below the
            form) - once this column stacks full-width under the left column on
            mobile, sticking it would fight the page's own scroll in a confusing
            way rather than acting like a cart summary. */}
        <div className="estimation-cost-panel" style={{ top: 20 }}>
          <Card>
            <Card.Body>
              <h6 className="mb-3">Estimated Cost</h6>

              {(addedOfferings.length > 0 || parts.length > 0) && (
                <div className="mb-3" style={{ maxHeight: 280, overflowY: "auto" }}>
                  {addedOfferings.map((o) => (
                    <div key={o.service_offering_id} className="mb-2 pb-2 border-bottom">
                      <div className="d-flex justify-content-between align-items-start" style={{ fontSize: 13, color: "#374151" }}>
                        <span className="fw-semibold">
                          {[o.service_name, o.service_type_name, o.service_offering_name].filter(Boolean).join(" - ")}
                        </span>
                        <span
                          role="button"
                          onClick={() => !isLocked && toggleOffering(o.service_offering_id)}
                          style={{ color: "#C0335A", cursor: isLocked ? "default" : "pointer", lineHeight: 1 }}
                        >
                          ✕
                        </span>
                      </div>
                      <div className="d-flex gap-2 align-items-center mt-1">
                        <Form.Control
                          type="number"
                          size="sm"
                          min="1"
                          value={o.quantity}
                          disabled={isLocked}
                          style={{ width: 60 }}
                          onChange={(e) => updateOfferingField(o.service_offering_id, "quantity", e.target.value)}
                          onBlur={(e) => {
                            const n = Math.max(1, parseInt(e.target.value, 10) || 1);
                            updateOfferingField(o.service_offering_id, "quantity", String(n));
                          }}
                        />
                        <span style={{ fontSize: 12, color: "#6B7280" }}>×</span>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          step="0.01"
                          value={o.unit_price}
                          disabled={isLocked}
                          style={{ width: 80 }}
                          onChange={(e) => updateOfferingField(o.service_offering_id, "unit_price", e.target.value)}
                          onBlur={(e) => {
                            const n = Math.max(0, parseFloat(e.target.value) || 0);
                            updateOfferingField(o.service_offering_id, "unit_price", String(n));
                          }}
                        />
                        <span className="ms-auto fw-semibold" style={{ fontSize: 13 }}>
                          {currency} {(Number(o.quantity) * Number(o.unit_price)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {parts.map((p) => (
                    <div key={p.id} className="d-flex justify-content-between" style={{ fontSize: 13, color: "#374151", marginBottom: 4 }}>
                      <span>{p.name}</span>
                      <span>{currency} {Number(p.amount).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              {addedOfferings.length === 0 && parts.length === 0 && (
                <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                  No services or parts added yet.
                </div>
              )}

              <Row className="g-2 mb-2">
                <Col xs={6}>
                  <Form.Label style={{ fontSize: 12 }}>Discount Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={values.discount_type}
                    onChange={(e) => setFieldValue("discount_type", e.target.value)}
                    disabled={isLocked}
                  >
                    <option value="FLAT">Flat</option>
                    <option value="PERCENT">Percent</option>
                  </Form.Select>
                </Col>
                <Col xs={6}>
                  <Form.Label style={{ fontSize: 12 }}>Discount Value</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    value={values.discount_value}
                    onChange={(e) => setFieldValue("discount_value", e.target.value)}
                    disabled={isLocked}
                  />
                </Col>
                <Col xs={6}>
                  <Form.Label style={{ fontSize: 12 }}>Tax %</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    value={values.tax_pct}
                    onChange={(e) => setFieldValue("tax_pct", e.target.value)}
                    disabled={isLocked}
                  />
                </Col>
                <Col xs={6}>
                  <Form.Label style={{ fontSize: 12 }}>Valid Until *</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    min={minValidUntil}
                    value={values.valid_until}
                    onChange={(e) => setFieldValue("valid_until", e.target.value)}
                    disabled={isLocked}
                    isInvalid={touched.valid_until && !!errors.valid_until}
                  />
                  <Form.Control.Feedback type="invalid">{errors.valid_until}</Form.Control.Feedback>
                </Col>
                <Col xs={12}>
                  <Form.Label style={{ fontSize: 12 }}>Notes</Form.Label>
                  <Form.Control
                    size="sm"
                    as="textarea"
                    rows={2}
                    value={values.notes}
                    onChange={(e) => setFieldValue("notes", e.target.value)}
                    disabled={isLocked}
                  />
                </Col>
              </Row>

              <div className="border-top mt-3 pt-3">
                <div className="d-flex justify-content-between">
                  <span>Subtotal</span>
                  <span>{currency} {subtotal.toFixed(2)}</span>
                </div>
                {Number(values.discount_value) > 0 && (
                  <div className="d-flex justify-content-between text-danger">
                    <span>Discount</span>
                    <span>-{currency} {discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {Number(values.tax_pct) > 0 && (
                  <div className="d-flex justify-content-between">
                    <span>Tax ({values.tax_pct}%)</span>
                    <span>{currency} {taxAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="d-flex justify-content-between fw-bold border-top mt-1 pt-1" style={{ fontSize: 18 }}>
                  <span>Grand Total</span>
                  <span>{currency} {grandTotal.toFixed(2)}</span>
                </div>
              </div>

              {!isLocked && (
                <div className="d-flex flex-column gap-2 mt-3">
                  <PillButton disabled={saving} onClick={() => handleSaveClick(true)}>
                    {saving ? "Saving…" : "Save & Send"}
                  </PillButton>
                  <TintButton tint="gray" disabled={saving} onClick={() => handleSaveClick(false)}>
                    Save as Draft
                  </TintButton>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </Col>
      </Row>

      <style>{`
        .estimation-cost-panel { position: static; }
        @media (min-width: 992px) {
          .estimation-cost-panel { position: sticky; }
        }
      `}</style>
    </div>
  );
}

export default EstimationForm;
